import i18n from '@/i18n';

export async function sleep(time) {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve();
    }, time);
  });
}

export function getUrlWithoutQuery() {
  const url = window.location.href.split('?')[0];
  return url;
}

export function getCookie(cookieName) {
  const cookie = {};
  document.cookie.split(';').forEach(function(el) {
    const [key, value] = el.split('=');
    cookie[key.trim()] = value;
  });
  return cookie[cookieName];
}

export function getUrlParams() {
  const historyUrl = document.location.href.replace(/\/#/g, ''); // To parse and remove any '#' from the URL.
  return new URL(historyUrl).searchParams;
}

export function getUrlParam(name) {
  const params = getUrlParams();
  return params.get(name);
}

export function getErrorMessage(err) {
  const status = err?.response?.status,
    code = err?.response?.data?.code,
    detail = err?.response?.data?.detail;

  const statusMessages = {
    401: i18n.t('user_not_authenticated'),
    403: i18n.t('user_not_authorized'),
    500: i18n.t('internal_server_error'),
    default: code ? i18n.t(code) : detail || err?.message
  };

  return statusMessages[status] || statusMessages.default;
}

export function getRequiredPlaceholder(value) {
  return `${i18n.t(value)} (${i18n.t('required')})`;
}

export function getValueWithSquareMeters(value) {
  if (!value) return null;
  return `${value} m²`;
}

export function currency(amount) {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat(i18n.locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0, // Allow numbers with no decimal places
    maximumFractionDigits: 2 // Maximum of 2 decimal places
  }).format(amount);
}

export function percentage(number) {
  if (!number) return '-';
  return new Intl.NumberFormat(i18n.locale, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(number / 100);
}

export async function recaptchaSubmit() {
  const grecaptcha = window.grecaptcha;
  const token = await new Promise(resolve => {
    grecaptcha.ready(async () => {
      const token = await grecaptcha.execute(
        process.env.VUE_APP_RECAPTCHA_SITE_KEY,
        { action: 'submit' }
      );
      resolve(token);
    });
  });
  return { 'Recaptcha-Token': token };
}

export function getFormattedDate(date) {
  return new Date(date).toLocaleString(i18n.locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

export function getFormattedTime(date) {
  return new Date(date).toLocaleString(i18n.locale, {
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function getFormattedShortMonthDate(date) {
  // The design has a date with short month name
  return new Date(date).toLocaleString(i18n.locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export function getMapboxStreetName(value) {
  return (
    value?.properties?.context?.street?.name ||
    value?.properties?.context?.name ||
    value?.properties?.name ||
    ''
  );
}

export function getISODateWithoutZ(datetime) {
  // We do this because BE is not timezone aware so we cannot use toISOString().
  // This method returns the date in an ISO like format, without the timezone.

  // Get local date components
  const year = datetime.getFullYear();
  const month = String(datetime.getMonth() + 1).padStart(2, '0');
  const day = String(datetime.getDate()).padStart(2, '0');
  const hours = String(datetime.getHours()).padStart(2, '0');
  const minutes = String(datetime.getMinutes()).padStart(2, '0');
  const seconds = String(datetime.getSeconds()).padStart(2, '0');

  // Format as YYYY-MM-DDTHH:mm:ss
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

export function toCalendarDate(milliseconds) {
  const date = new Date(milliseconds);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  // Return YYYY-MM-DD format without timezone conversion
  return `${year}-${month}-${day}`;
}

/**
 * Formats a date into DD/MM/YYYY string format
 * @param {Date|string} date - Date object or date string to format
 * @returns {string} Date formatted as DD/MM/YYYY
 */
export function formatDateToDDMMYYYY(date) {
  const d = new Date(date);
  // Get day and pad with leading zero if needed
  const day = String(d.getDate()).padStart(2, '0');
  // Get month (0-based) and pad with leading zero if needed
  const month = String(d.getMonth() + 1).padStart(2, '0');
  // Get full year
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

export function getFileSrc(file) {
  return URL.createObjectURL(file);
}
